/*!
 * @virtual-list/vue
 * Author: phphe <phphe@outlook.com> (https://github.com/phphe)
 * Homepage: https://virtual-list.phphe.com
 * Released under the MIT License.
 */
import { defineComponent, ref, computed, reactive, watch, onMounted, nextTick } from "vue-demi";
import * as hp from "helper-js";
import { openBlock, createElementBlock, renderSlot, createElementVNode, resolveComponent, normalizeStyle, createVNode, withCtx, Fragment, renderList } from "vue";
var _export_sfc = (sfc, props) => {
  const target = sfc.__vccOpts || sfc;
  for (const [key, val] of props) {
    target[key] = val;
  }
  return target;
};
const _sfc_main$1 = defineComponent({
  props: {
    table: Boolean
  }
});
const _hoisted_1 = { key: 0 };
const _hoisted_2 = { key: 1 };
function _sfc_render$1(_ctx, _cache, $props, $setup, $data, $options) {
  return _ctx.table ? (openBlock(), createElementBlock("table", _hoisted_1, [
    renderSlot(_ctx.$slots, "prepend"),
    createElementVNode("tbody", null, [
      renderSlot(_ctx.$slots, "default")
    ]),
    renderSlot(_ctx.$slots, "append")
  ])) : (openBlock(), createElementBlock("div", _hoisted_2, [
    renderSlot(_ctx.$slots, "prepend"),
    renderSlot(_ctx.$slots, "default"),
    renderSlot(_ctx.$slots, "append")
  ]));
}
var VirtualListTable = /* @__PURE__ */ _export_sfc(_sfc_main$1, [["render", _sfc_render$1]]);
const cpt = defineComponent({
  components: { VirtualListTable },
  props: {
    items: Array,
    disabled: Boolean,
    horizontal: Boolean,
    firstRender: { type: Number, default: 10 },
    buffer: { type: Number, default: 100 },
    itemKey: {
      type: [String, Function]
    },
    itemSize: {
      type: Function
    },
    table: Boolean
  },
  setup(props) {
    const start = ref(0);
    const end = ref(props.firstRender - 1);
    const end2 = computed(() => {
      var _a;
      return hp.notGreaterThan(end.value, (((_a = props.items) == null ? void 0 : _a.length) || 1) - 1);
    });
    const avgSize = ref(0);
    const startSize = computed(() => positions.value[start.value] ? getPosition(start.value) : 0);
    const totalSize = computed(() => positions.value.length > 0 ? getPosition(positions.value.length - 1) + hp.arrayLast(sizes.value) : 0);
    const endSize = computed(() => positions.value[end2.value] ? totalSize.value - getPosition(end2.value) - sizes.value[end2.value] : 0);
    const listStyle = computed(() => !props.disabled ? { overflow: "auto" } : {});
    const listInnerStyle = computed(() => {
      const r = {
        display: "flex"
      };
      if (!props.disabled) {
        if (!props.horizontal) {
          Object.assign(r, {
            "margin-top": startSize.value + "px",
            "margin-bottom": endSize.value + "px"
          });
        } else {
          Object.assign(r, {
            "margin-left": startSize.value + "px",
            "margin-right": endSize.value + "px",
            width: totalSize.value - endSize.value - startSize.value + "px"
          });
        }
      }
      r["flex-direction"] = !props.horizontal ? "column" : "row";
      if (props.table) {
        delete r.display;
        delete r["flex-direction"];
      }
      return r;
    });
    const runtimeSizes = computed(() => reactive((props.items || []).map(() => null)));
    const sizes = computed(() => (props.items || []).map((item, index) => {
      var _a;
      if (runtimeSizes[index] != null) {
        return runtimeSizes[index];
      }
      let r = (_a = props.itemSize) == null ? void 0 : _a.call(props, item, index);
      if (r == null) {
        r = avgSize.value;
      }
      return r;
    }));
    const positions = computed(() => {
      const p = [];
      sizes.value.reduce((a, b) => {
        p.push(a);
        return a + b;
      }, 0);
      return p;
    });
    watch(() => props.items, update);
    const visibleItemsInfo = computed(() => {
      if (!props.items || props.disabled) {
        return;
      }
      const r = [];
      for (let index = start.value; index <= end2.value; index++) {
        const item = props.items[index];
        if (!item) {
          break;
        }
        r.push({ item, index });
      }
      return r;
    });
    const listElRef = ref();
    const listInnerRef = ref();
    onMounted(async () => {
      update();
      try {
        createResizeObserver();
      } catch (error) {
        await nextTick();
        update();
      }
    });
    let prevScroll;
    function onscroll() {
      const listEl = listElRef.value;
      if (!listEl) {
        return;
      }
      const currentScroll = getScroll(listEl);
      if (prevScroll != null && props.buffer - Math.abs(currentScroll - prevScroll) >= 10) {
        return;
      }
      prevScroll = currentScroll;
      update();
    }
    let executing = false;
    let waiting = false;
    async function update() {
      var _a;
      if (executing) {
        waiting = true;
        return;
      }
      if (!props.items || props.disabled) {
        return;
      }
      executing = true;
      const listEl = listElRef.value;
      const listInner = (_a = listInnerRef.value) == null ? void 0 : _a.$el;
      if (!listEl || !listInner) {
        return;
      }
      if (!avgSize.value) {
        avgSize.value = getAvgSize();
      }
      start.value = getStart();
      end.value = getEnd();
      await nextTick();
      let updated;
      let vi0 = 0;
      const runtimeSizesTemp = {};
      const children = !props.table ? listInner.children : listInner.querySelector("tbody").children;
      for (let i = 0; i < children.length; i++) {
        const el = children[i];
        const cssPosition = hp.css(el, "position");
        if (cssPosition && ["absolute", "fixed"].includes(cssPosition)) {
          continue;
        }
        const size = hp.css(el, "display") !== "none" ? getOuterSize(el) : 0;
        const vi = el.getAttribute("vt-index");
        const index = vi ? parseInt(vi) : start.value + vi0;
        runtimeSizesTemp[index] = (runtimeSizesTemp[index] || 0) + size;
        vi0++;
      }
      for (const indexS of Object.keys(runtimeSizesTemp)) {
        const index = parseInt(indexS);
        if (runtimeSizes.value[index] !== runtimeSizesTemp[index]) {
          runtimeSizes.value[index] = runtimeSizesTemp[index];
          updated = true;
        }
      }
      if (updated) {
        await nextTick();
      }
      executing = false;
      if (waiting) {
        waiting = false;
        update();
      }
      function getStart() {
        const startPosition = getScroll(listEl) - getPaddingStart(listEl) - props.buffer;
        const r = hp.binarySearch(positions.value, (mid) => mid - startPosition, { returnNearestIfNoHit: true });
        return r.index;
      }
      function getEnd() {
        const endPosition = getScroll(listEl) - getPaddingStart(listEl) + getClientSize(listEl) + props.buffer;
        const r = hp.binarySearch(positions.value, (mid) => mid - endPosition, { returnNearestIfNoHit: true });
        return r.index;
      }
      function getAvgSize() {
        const maxSampleCount = 10;
        const sizeArr = [];
        const children2 = !props.table ? listInner.children : listInner.querySelector("tbody").children;
        for (let index = 0; index < children2.length; index++) {
          const el = children2[index];
          const style = getComputedStyle(el);
          if (["absolute", "fixed"].includes(style.position)) {
            continue;
          }
          const outerSize = getOuterSize(el);
          sizeArr.push(outerSize);
          if (sizeArr.length >= maxSampleCount) {
            break;
          }
        }
        if (sizeArr.length === 0) {
          return 0;
        }
        return sizeArr.reduce((a, b) => a + b, 0) / sizeArr.length;
      }
    }
    function getClientSize(el) {
      const style = getComputedStyle(el);
      let r = parseFloat(!props.horizontal ? style.height : style.width);
      if (style.boxSizing === "border-box") {
        if (!props.horizontal) {
          r = r - parseFloat(style.borderTopWidth) - parseFloat(style.borderBottomWidth);
        } else {
          r = r - parseFloat(style.borderLeftWidth) - parseFloat(style.borderRightWidth);
        }
      }
      return r;
    }
    function getOuterSize(el) {
      let r = getClientSize(el);
      const style = getComputedStyle(el);
      if (!props.horizontal) {
        r += parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth) + parseFloat(style.marginTop) + parseFloat(style.marginBottom);
      } else {
        r += parseFloat(style.borderLeftWidth) + parseFloat(style.borderRightWidth) + parseFloat(style.marginLeft) + parseFloat(style.marginRight);
      }
      r = Number.isNaN(r) ? 0 : r;
      return r;
    }
    function getScroll(el) {
      return !props.horizontal ? el.scrollTop : el.scrollLeft;
    }
    function getPaddingStart(el) {
      const style = getComputedStyle(el);
      return !props.horizontal ? parseFloat(style.paddingTop) : parseFloat(style.paddingLeft);
    }
    function getPosition(index) {
      return positions.value[index];
    }
    function createResizeObserver() {
      const listEl = listElRef.value;
      const resizeObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
          if (hp.hasClass(entry.target, "vtlist")) {
            update();
            break;
          }
        }
      });
      resizeObserver.observe(listEl);
    }
    function getItemKey(item, index) {
      if (props.itemKey) {
        if (typeof props.itemKey === "string" && props.itemKey === "index") {
          return index;
        } else if (typeof props.itemKey === "function") {
          return props.itemKey(item, index);
        }
      }
    }
    return {
      listElRef,
      listInnerRef,
      onscroll,
      listStyle,
      listInnerStyle,
      visibleItemsInfo,
      getItemKey,
      update,
      sizes,
      positions,
      runtimeSizes
    };
  }
});
const _sfc_main = cpt;
function _sfc_render(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_VirtualListTable = resolveComponent("VirtualListTable");
  return openBlock(), createElementBlock("div", {
    class: "vtlist",
    ref: "listElRef",
    style: normalizeStyle(_ctx.listStyle),
    onScrollPassive: _cache[0] || (_cache[0] = (...args) => _ctx.onscroll && _ctx.onscroll(...args))
  }, [
    createVNode(_component_VirtualListTable, {
      class: "vtlist-inner",
      ref: "listInnerRef",
      style: normalizeStyle(_ctx.listInnerStyle),
      table: _ctx.table
    }, {
      prepend: withCtx(() => [
        renderSlot(_ctx.$slots, "prepend")
      ]),
      append: withCtx(() => [
        renderSlot(_ctx.$slots, "append")
      ]),
      default: withCtx(() => [
        _ctx.disabled ? (openBlock(true), createElementBlock(Fragment, { key: 0 }, renderList(_ctx.items, (item, index) => {
          return renderSlot(_ctx.$slots, "default", {
            key: _ctx.getItemKey(item, index),
            item,
            index
          });
        }), 128)) : (openBlock(true), createElementBlock(Fragment, { key: 1 }, renderList(_ctx.visibleItemsInfo, ({ item, index }) => {
          return renderSlot(_ctx.$slots, "default", {
            key: _ctx.getItemKey(item, index),
            item,
            index
          });
        }), 128))
      ]),
      _: 3
    }, 8, ["style", "table"])
  ], 36);
}
var VirtualList = /* @__PURE__ */ _export_sfc(_sfc_main, [["render", _sfc_render]]);
export { VirtualList as default };
